import { useState } from "react";
import { Instagram, Linkedin, Mail, Globe, Handshake, Send } from "lucide-react";
import { Toaster, toast } from "sonner";
import { PageHero, Reveal, SectionHead } from "@/components/Shared";
import { api, formatApiErrorDetail, usePageContent, IMG } from "@/lib/api";
import { PAGES } from "@/content/pageContent";

function getSocialHref(label, val) {
  if (!val || val.startsWith("[")) return null;
  if (label === "Instagram") {
    if (val.startsWith("http")) return val;
    const handle = val.replace(/^@/, "").trim();
    return `https://instagram.com/${handle}`;
  }
  if (label === "Email") {
    if (val.includes("@")) return `mailto:${val.trim()}`;
    return null;
  }
  if (label === "LinkedIn") {
    if (val.startsWith("http")) return val;
    return `https://linkedin.com/in/${val.trim()}`;
  }
  if (label === "College Website") {
    if (val.startsWith("http")) return val;
    return "https://sophiacollegemumbai.com";
  }
  if (val.startsWith("http")) return val;
  return null;
}

export default function Contact() {
  const c = usePageContent("contact", PAGES.contact.defaults);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const SOCIALS = [
    { icon: Instagram, label: "Instagram", note: c.instagram },
    { icon: Linkedin, label: "LinkedIn", note: c.linkedin },
    { icon: Mail, label: "Email", note: c.email },
    { icon: Globe, label: "College Website", note: c.college },
    { icon: Handshake, label: "Rotary Connection", note: c.rotary },
  ];

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we will get back to you soon!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <main>
      <PageHero eyebrow="Contact / Connect" title="Connect with" italic="RCSCW" image={IMG.celestialSky}>
        <p className="text-sm leading-relaxed text-silver-dim sm:text-base">
          {c.heroText}
        </p>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 py-20" data-testid="contact-section">
        <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <SectionHead eyebrow="Get in Touch" title="Send us a" italic="message" />
            <Reveal delay={0.15}>
              <form onSubmit={submit} className="card-line mt-10 space-y-5 p-8" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="eyebrow mb-2 block" htmlFor="contact-name">Your Name</label>
                    <input id="contact-name" required className="input-field" placeholder="Full name" value={form.name} onChange={set("name")} data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="eyebrow mb-2 block" htmlFor="contact-email">Email</label>
                    <input id="contact-email" required type="email" className="input-field" placeholder="you@example.com" value={form.email} onChange={set("email")} data-testid="contact-email-input" />
                  </div>
                </div>
                <div>
                  <label className="eyebrow mb-2 block" htmlFor="contact-subject">Subject</label>
                  <input id="contact-subject" className="input-field" placeholder="Collaboration, membership, invitation…" value={form.subject} onChange={set("subject")} data-testid="contact-subject-input" />
                </div>
                <div>
                  <label className="eyebrow mb-2 block" htmlFor="contact-message">Message</label>
                  <textarea id="contact-message" required rows={5} className="input-field resize-none" placeholder="Tell us what you have in mind…" value={form.message} onChange={set("message")} data-testid="contact-message-input" />
                </div>
                <button type="submit" disabled={sending} className="btn-ember disabled:opacity-60" data-testid="contact-submit-btn">
                  <span>{sending ? "Sending…" : "Send Message"}</span> <Send size={14} />
                </button>
              </form>
            </Reveal>
          </div>

          <div>
            <SectionHead eyebrow="Find Us" title="Where to" italic="reach us" />
            <div className="mt-10 space-y-4">
              {SOCIALS.map((s, i) => {
                const href = getSocialHref(s.label, s.note);
                const CardTag = href ? "a" : "div";
                return (
                  <Reveal key={s.label} delay={i * 0.07}>
                    <CardTag
                      href={href || undefined}
                      target={href && !href.startsWith("mailto:") ? "_blank" : undefined}
                      rel={href && !href.startsWith("mailto:") ? "noopener noreferrer" : undefined}
                      className={`card-line group flex items-center gap-5 p-5 ${href ? "cursor-pointer transition-all hover:border-silver/40 hover:bg-navy/60" : ""}`}
                      data-testid={`contact-social-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-silver/20 text-silver-dim transition-colors group-hover:border-ember/50 group-hover:text-ember">
                        <s.icon size={17} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-silver">{s.label}</p>
                        <p className="truncate text-xs text-silver-muted">{s.note}</p>
                      </div>
                    </CardTag>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={0.3}>
              <div className="corner-frame mt-10 border border-silver/20 bg-navy/50 p-8">
                <p className="font-display text-2xl italic text-silver">{c.addressTitle}</p>
                <p className="mt-3 text-sm leading-relaxed text-silver-muted">
                  {c.addressLine}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <Toaster position="bottom-right" theme="dark" />
    </main>
  );
}
